import { useEffect, useMemo, useState } from "react";

import { CircularProgress } from "@material-ui/core";
import ListSubheader from "@material-ui/core/ListSubheader";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Collapse from "@material-ui/core/Collapse";
import WbIncandescentIcon from "@material-ui/icons/WbIncandescent";
import ToysIcon from "@material-ui/icons/Toys";
import BorderTopIcon from "@material-ui/icons/BorderTop";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import { QUERIES } from "../../../graphql";
import { useSnackbarLazyQuery } from "../../../hooks";

import useDevicesEditorStyles from "./room_devices_editor.style";
import { Apartment, Device, DeviceType, Room } from "../../../graphql/models";
import { DeviceEditor } from "../device_editor";

export interface RoomDevicesEditorProps {
  apartment: Apartment;
  room: Room;
  onDeviceUpdated?: (device: Device) => void;
}

export function RoomDevicesEditor(props: RoomDevicesEditorProps) {
  const { apartment, room, onDeviceUpdated } = props;
  const classes = useDevicesEditorStyles();

  const [invokeGetDevices, { data: devicesData, loading, refetch }] =
    useSnackbarLazyQuery(QUERIES.DEVICES);

  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const isItemExpanded = (deviceId: string) => expandedItems.includes(deviceId);
  const toggleExpandItem = (deviceId: string) => {
    const itemIndex = expandedItems.indexOf(deviceId);
    if (itemIndex === -1) {
      setExpandedItems([...expandedItems, deviceId]);
    } else {
      const expandedItemsCopy = [...expandedItems];
      expandedItemsCopy.splice(itemIndex, 1);
      setExpandedItems([...expandedItemsCopy]);
    }
  };

  useEffect(() => {
    if (room) {
      invokeGetDevices({
        variables: {
          roomId: room.id,
        },
      });
    }
  }, [room, invokeGetDevices]);

  const devices: Device[] | undefined = useMemo(() => {
    if (devicesData) {
      return devicesData.devices;
    }

    return void 0;
  }, [devicesData]);

  const guessDeviceIcon = (device: Device) => {
    switch (device.type) {
      case DeviceType.DIMMER_LIGHT:
      case DeviceType.LIGHT:
        return <WbIncandescentIcon />;
      case DeviceType.FAN:
        return <ToysIcon />;
      case DeviceType.SHUTTER:
        return <BorderTopIcon />;
      default:
        return <HelpOutlineIcon />;
    }
  };

  const onNestedDeviceUpdated = (device: Device) => {
    if (refetch) {
      refetch();
    }
    if (onDeviceUpdated) {
      onDeviceUpdated(device);
    }
  };

  return (
    <>
      {loading && <CircularProgress />}
      {!loading && (
        <List
          component="nav"
          aria-labelledby={`room-${room.id}-devices-list-subheader`}
          subheader={
            <ListSubheader
              component="div"
              id={`room-${room.id}-devices-list-subheader`}
            >
              Elenco dispositivi
            </ListSubheader>
          }
          style={{ width: "100%" }}
        >
          {devices?.map((device) => (
            <>
              <ListItem
                button
                onClick={() => toggleExpandItem(device.id ?? "")}
              >
                <ListItemIcon>{guessDeviceIcon(device)}</ListItemIcon>
                <ListItemText primary={device.name} />
                {isItemExpanded(device.id ?? "") ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </ListItem>
              <Collapse
                in={isItemExpanded(device.id ?? "")}
                timeout="auto"
                unmountOnExit
              >
                <DeviceEditor
                  className={classes.nested}
                  apartment={apartment}
                  device={device}
                  room={room}
                  onDeviceUpdated={onNestedDeviceUpdated}
                />
              </Collapse>
            </>
          ))}
        </List>
      )}
    </>
  );
}
